import React, { useCallback, useState } from 'react';
import { X, RefreshCw } from 'lucide-react';
import { useLazyCommand, useDomainEvent } from '@soap/modules';

interface RegenerateLessonMaterialDialogProps {
    unitId: string;
    lessonId: string;
    stepId: string;
    materialType: '10' | '20';
    materialTitle: string;
    onClose: (wasConfirmed: boolean) => void;
}

const viewStates = {
    ready: 0,
    submitting: 1
};

function materialKindLabel(materialType: '10' | '20'): string {
    return materialType === '10' ? 'worksheet' : 'reading';
}

export function RegenerateLessonMaterialDialog({
    unitId,
    lessonId,
    stepId,
    materialType,
    materialTitle,
    onClose
}: RegenerateLessonMaterialDialogProps) {
    const [viewState, setViewState] = useState(viewStates.ready);
    const [sendRegenerateLessonMaterialCommand] = useLazyCommand('C440v1_RegenerateLessonMaterial');
    const triggerCloseContextMenu = useDomainEvent({
        eventName: 'close-context-menu'
    });

    const kindLabel = materialKindLabel(materialType);

    const handleClose = useCallback(
        (wasConfirmed: boolean = false) => {
            setViewState(viewStates.ready);
            onClose(wasConfirmed);
            triggerCloseContextMenu();
        },
        [onClose, triggerCloseContextMenu]
    );

    const handleRegenerate = async () => {
        setViewState(viewStates.submitting);

        try {
            await sendRegenerateLessonMaterialCommand({
                C440_UnitId: unitId,
                C440_LessonId: lessonId,
                C440_StepId: stepId,
                C440_MaterialType: materialType
            });
            handleClose(true);
        } catch (error) {
            console.error('Error regenerating lesson material:', error);
            setViewState(viewStates.ready);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-gray-900">
                            Regenerate {kindLabel === 'worksheet' ? 'Worksheet' : 'Reading'}
                        </h2>
                        <button
                            type="button"
                            onClick={() => handleClose(false)}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                            disabled={viewState === viewStates.submitting}
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>

                    <div className="space-y-4">
                        <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                            <p className="text-sm text-orange-800">This action cannot be undone.</p>
                            <p className="text-sm text-orange-800 mt-2">
                                Please confirm you want to regenerate this {kindLabel}
                                {materialTitle ? (
                                    <>
                                        {' '}
                                        (<strong>{materialTitle}</strong>
                                    </>
                                ) : null}
                                ?
                            </p>
                            <p className="text-sm text-orange-800 mt-2">
                                The lesson structure stays the same. Only this {kindLabel} (and its related
                                figures or diagrams) is rewritten using the same generation pipeline as create.
                            </p>
                        </div>

                        <div className="bg-action-blue/10 p-4 rounded-lg border border-action-blue/30">
                            <h4 className="font-medium text-action-blue-dark mb-2 flex items-center">
                                <RefreshCw size={16} className="mr-2" />
                                What happens:
                            </h4>
                            <ul className="ml-6 space-y-1 list-disc">
                                <li className="text-sm text-action-blue-dark">
                                    This {kindLabel}&apos;s text is replaced
                                </li>
                                <li className="text-sm text-action-blue-dark">
                                    Figures and diagrams for this {kindLabel} are regenerated
                                </li>
                                <li className="text-sm text-action-blue-dark">
                                    Detailed instructions for this step are rewritten to match
                                </li>
                                <li className="text-sm text-action-blue-dark">
                                    Other worksheets and readings in the lesson stay unchanged
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="flex justify-end space-x-3 pt-6">
                        <button
                            type="button"
                            onClick={() => handleClose(false)}
                            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                            disabled={viewState === viewStates.submitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={() => void handleRegenerate()}
                            className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                            disabled={viewState === viewStates.submitting}
                        >
                            <RefreshCw size={16} className={`mr-2 ${viewState === viewStates.submitting ? 'animate-spin' : ''}`} />
                            {viewState === viewStates.submitting ? 'Regenerating...' : 'Regenerate'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
